// Utility functions for data validation and sanitization
export const validateEmail = (
  email: string,
): { isValid: boolean; error?: string } => {
  if (!email || email.trim().length === 0) {
    return { isValid: false, error: "Email is required" }
  }

  // Enhanced email validation
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" }
  }

  // Check for potentially dangerous characters
  if (
    email.includes("<") ||
    email.includes(">") ||
    email.includes('"') ||
    email.includes("'")
  ) {
    return { isValid: false, error: "Email contains invalid characters" }
  }

  // Check email length (RFC 5321 limit)
  if (email.length > 254) {
    return { isValid: false, error: "Email address is too long" }
  }

  return { isValid: true }
}

/**
 * Validates that an email address is from the @condri.app domain
 * Used for staff-only signup restrictions
 */
export const validateCondriEmail = (
  email: string,
): { isValid: boolean; error?: string } => {
  const baseValidation = validateEmail(email)
  if (!baseValidation.isValid) {
    return baseValidation
  }

  const normalizedEmail = email.toLowerCase().trim()
  if (!normalizedEmail.endsWith("@condri.app")) {
    return {
      isValid: false,
      error: "Only @condri.app email addresses are allowed for signup",
    }
  }

  return { isValid: true }
}

export const validatePassword = (
  password: string,
): {
  isValid: boolean
  error?: string
  strength?: "weak" | "medium" | "strong"
} => {
  if (!password) {
    return { isValid: false, error: "Password is required" }
  }

  if (password.length < 8) {
    return {
      isValid: false,
      error: "Password must be at least 8 characters long",
    }
  }

  if (password.length > 128) {
    return {
      isValid: false,
      error: "Password must be less than 128 characters",
    }
  }

  // Check for basic requirements
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

  const requirementsMet = [
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar,
  ].filter(Boolean).length

  // Determine strength
  let strength: "weak" | "medium" | "strong" = "weak"
  if (password.length >= 12 && requirementsMet >= 3) {
    strength = "strong"
  } else if (password.length >= 10 && requirementsMet >= 2) {
    strength = "medium"
  }

  return { isValid: true, strength }
}

export const validateName = (
  name: string,
): { isValid: boolean; error?: string } => {
  if (!name.trim()) {
    return { isValid: false, error: "Name is required" }
  }

  if (name.trim().length < 1) {
    return { isValid: false, error: "Name cannot be empty" }
  }

  if (name.length > 100) {
    return { isValid: false, error: "Name must be less than 100 characters" }
  }

  // Check for potentially harmful characters
  const dangerousChars = /<script|javascript:|data:|vbscript:|on\w+=/i
  if (dangerousChars.test(name)) {
    return { isValid: false, error: "Name contains invalid characters" }
  }

  return { isValid: true }
}

export const sanitizeText = (text: string): string => {
  // Remove potentially dangerous HTML/script content
  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/javascript:/gi, "")
    .replace(/on\w+\s*=/gi, "")
    .replace(/data:/gi, "")
    .replace(/vbscript:/gi, "")
    .trim()
}

// Validate user agent and referrer strings
export const validateUserAgent = (
  userAgent: string,
): { isValid: boolean; error?: string } => {
  if (!userAgent || userAgent.trim().length === 0) {
    return { isValid: true } // Optional field
  }

  // Check for reasonable length (most user agents are under 512 chars)
  if (userAgent.length > 1000) {
    return { isValid: false, error: "User agent string is too long" }
  }

  // Check for suspicious patterns
  const suspiciousPatterns = ["<script", "javascript:", "data:", "vbscript:"]
  const lowerUserAgent = userAgent.toLowerCase()
  for (const pattern of suspiciousPatterns) {
    if (lowerUserAgent.includes(pattern)) {
      return { isValid: false, error: "User agent contains invalid content" }
    }
  }

  return { isValid: true }
}

export const validateReferrer = (
  referrer: string,
): { isValid: boolean; error?: string } => {
  if (!referrer || referrer.trim().length === 0) {
    return { isValid: true } // Optional field
  }

  // Check for reasonable length
  if (referrer.length > 2048) {
    return { isValid: false, error: "Referrer URL is too long" }
  }

  // Basic URL validation if provided
  try {
    if (referrer !== "direct") {
      new URL(referrer)
    }
  } catch {
    return { isValid: false, error: "Invalid referrer URL" }
  }

  return { isValid: true }
}

export const validateSymptomEntry = (entry: {
  symptom: string
  physicalIntensity: number
  worryLevel: number
  context: string[]
  predictedDuration: string
  notes?: string
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Validate symptom
  if (!entry.symptom.trim()) {
    errors.push("Please describe what you're feeling")
  } else if (entry.symptom.length > 500) {
    errors.push("Symptom description must be less than 500 characters")
  }

  // Validate intensity levels
  if (entry.physicalIntensity < 1 || entry.physicalIntensity > 5) {
    errors.push("Physical intensity must be between 1 and 5")
  }

  if (entry.worryLevel < 1 || entry.worryLevel > 5) {
    errors.push("Worry level must be between 1 and 5")
  }

  // Validate context
  if (entry.context.length > 10) {
    errors.push("Please select no more than 10 context items")
  }

  // Validate predicted duration
  if (!entry.predictedDuration.trim()) {
    errors.push("Please select a predicted duration")
  }

  // Validate notes if provided
  if (entry.notes && entry.notes.length > 1000) {
    errors.push("Notes must be less than 1000 characters")
  }

  return { isValid: errors.length === 0, errors }
}

export const validateCheckIn = (checkIn: {
  mood_rating?: number
  anxiety_level?: number
  sleep_quality?: number
  notes?: string
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Validate ratings if provided
  if (
    checkIn.mood_rating !== undefined &&
    (checkIn.mood_rating < 1 || checkIn.mood_rating > 5)
  ) {
    errors.push("Mood rating must be between 1 and 5")
  }

  if (
    checkIn.anxiety_level !== undefined &&
    (checkIn.anxiety_level < 1 || checkIn.anxiety_level > 5)
  ) {
    errors.push("Anxiety level must be between 1 and 5")
  }

  if (
    checkIn.sleep_quality !== undefined &&
    (checkIn.sleep_quality < 1 || checkIn.sleep_quality > 5)
  ) {
    errors.push("Sleep quality must be between 1 and 5")
  }

  // Validate notes if provided
  if (checkIn.notes && checkIn.notes.length > 2000) {
    errors.push("Notes must be less than 2000 characters")
  }

  return { isValid: errors.length === 0, errors }
}

// Rate limiting utilities
const rateLimitMap = new Map<string, { count: number; resetTime: number }>()

export const checkRateLimit = (
  key: string,
  maxRequests = 5,
  windowMs = 60000,
): boolean => {
  const now = Date.now()
  const windowStart = now - windowMs

  const current = rateLimitMap.get(key)

  if (!current || current.resetTime < windowStart) {
    rateLimitMap.set(key, { count: 1, resetTime: now + windowMs })
    return true
  }

  if (current.count >= maxRequests) {
    return false
  }

  current.count++
  return true
}

// Error handling utilities
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === "string") {
    return error
  }

  if (error && typeof error === "object" && "message" in error) {
    return String(error.message)
  }

  return "An unexpected error occurred"
}

export const isDatabaseError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes("Failed to fetch") ||
      error.message.includes("NetworkError") ||
      error.message.includes("timeout") ||
      error.message.includes("connection")
    )
  }
  return false
}

export const isAuthError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return (
      error.message.includes("Invalid login credentials") ||
      error.message.includes("User not found") ||
      error.message.includes("Email not confirmed") ||
      error.message.includes("Too many requests")
    )
  }
  return false
}
